/**
 * Reactive post-pay auth outcome for already-mounted Refund/QR CTAs.
 * Same-tab sessionStorage writes notify via CustomEvent + useSyncExternalStore.
 * Prefer {@link useHasSucceededPostPayAuthOutcome} for keyed boolean checks.
 */

import { useMemo, useSyncExternalStore } from 'react';
import {
  getPostPayAuthActionOutcomeServerSnapshot,
  getPostPayAuthActionOutcomeSnapshot,
  hasSucceededPostPayAuthOutcome,
  peekPostPayAuthActionOutcome,
  subscribePostPayAuthActionOutcome,
  type PostPayAuthActionOutcome,
  type PostPayAuthActionOutcomeKey,
} from './postPayPendingAuthAction.js';

/** Live outcome for one `kind` + `paymentId` map entry. */
export function usePostPayAuthActionOutcome(
  input: PostPayAuthActionOutcomeKey,
): PostPayAuthActionOutcome | null {
  const { kind, paymentId } = input;
  const raw = useSyncExternalStore(
    subscribePostPayAuthActionOutcome,
    getPostPayAuthActionOutcomeSnapshot,
    getPostPayAuthActionOutcomeServerSnapshot,
  );
  return useMemo((): PostPayAuthActionOutcome | null => {
    void raw;
    return peekPostPayAuthActionOutcome({ kind, paymentId });
  }, [raw, kind, paymentId]);
}

/** True when durable outcome succeeded for this payment + kind (live). */
export function useHasSucceededPostPayAuthOutcome(
  input: PostPayAuthActionOutcomeKey,
): boolean {
  const { kind, paymentId } = input;
  const raw = useSyncExternalStore(
    subscribePostPayAuthActionOutcome,
    getPostPayAuthActionOutcomeSnapshot,
    getPostPayAuthActionOutcomeServerSnapshot,
  );
  return useMemo((): boolean => {
    void raw;
    return hasSucceededPostPayAuthOutcome({ kind, paymentId });
  }, [raw, kind, paymentId]);
}
